import type { IntegrationConnectionDto } from '@mesterplan/api-client';

/**
 * Pure helpers behind the Trackrs card on Marketplace and the
 * `/integrations/connect` acceptor page. Kept free of React and of the API
 * client so the decisions (which row is "the" connection, who may accept, what
 * to tell the user on a 403/409/502, how the handshake survives a login round
 * trip) are unit-testable in isolation.
 */

export const TRACKRS_PRODUCT = 'trackrs';

/** The remote products this web app knows how to talk about. */
const PRODUCT_LABELS: Record<string, string> = {
  trackrs: 'Trackrs',
};

export function productLabel(product: string): string {
  return PRODUCT_LABELS[product] ?? product;
}

export function isKnownProduct(product: string | null): product is string {
  return product !== null && Object.prototype.hasOwnProperty.call(PRODUCT_LABELS, product);
}

/**
 * The list endpoint returns every row for the company, revoked ones included and
 * newest first (design doc Q4: a disconnected connection keeps its row). The card
 * only cares about the one that is still in play; a revoked row means "not
 * connected", so the user can start over.
 */
export function findLiveConnection(
  connections: ReadonlyArray<IntegrationConnectionDto> | null | undefined,
  remoteProduct: string,
): IntegrationConnectionDto | null {
  if (!connections) return null;
  return (
    connections.find((c) => c.remoteProduct === remoteProduct && c.status !== 'revoked') ?? null
  );
}

export interface ConnectionHealth {
  /** Short status line under the card title. */
  headline: string;
  /** Secondary line: last sync / last error, or null when there is nothing to say yet. */
  detail: string | null;
  tone: 'ok' | 'pending' | 'error';
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * What the connected card says. `error` rows are still linked (the remote side
 * knows about them) but the last call failed, so they read as connected-with-a-
 * problem rather than as disconnected.
 */
export function describeConnection(connection: IntegrationConnectionDto): ConnectionHealth {
  const product = productLabel(connection.remoteProduct);
  const remoteName = connection.remoteCompanyName?.trim();

  if (connection.status === 'pending') {
    return {
      headline: `Afventer bekræftelse i ${product}`,
      detail: 'Forbindelsen er startet, men endnu ikke godkendt. Gennemfør den i ' +
        `${product}, eller annuller og start forfra.`,
      tone: 'pending',
    };
  }

  const headline = remoteName ? `Forbundet med ${remoteName}` : `Forbundet med ${product}`;

  // A remembered error outranks an older success: the user needs to know the
  // link is not delivering right now, whatever the row's status says.
  if (connection.lastError) {
    return { headline, detail: `Seneste fejl: ${connection.lastError}`, tone: 'error' };
  }
  if (connection.lastSuccessAt) {
    return {
      headline,
      detail: `Sidst synkroniseret: ${formatDateTime(connection.lastSuccessAt)}`,
      tone: 'ok',
    };
  }
  if (connection.activatedAt) {
    return {
      headline,
      detail: `Forbundet ${formatDateTime(connection.activatedAt)} — endnu ingen synkronisering.`,
      tone: 'ok',
    };
  }
  return { headline, detail: null, tone: 'ok' };
}

// --- Acceptor page --------------------------------------------------------------------------

/** The query string Trackrs sends the user here with. */
export interface ConnectParams {
  from: string;
  connection: string;
}

const CONNECT_STORAGE_KEY = 'mp.integrations.connect';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function parseConnectParams(search: string | URLSearchParams): ConnectParams | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const from = params.get('from')?.trim().toLowerCase() ?? '';
  const connection = params.get('connection')?.trim() ?? '';
  if (!from || !connection) return null;
  return { from, connection };
}

/**
 * Remembers the handshake across the pages that sit between the link and the
 * accept call: the IDP login round trip and the create-first-company screen. The
 * URL is the primary carrier (the login returnUrl keeps it, and onboarding never
 * changes it); this is the fallback for the cases where it is lost. Session
 * scope on purpose — a handshake expires in 15 minutes, so nothing should
 * outlive the tab.
 */
export function rememberConnectParams(params: ConnectParams, storage: StorageLike): void {
  try {
    storage.setItem(CONNECT_STORAGE_KEY, JSON.stringify(params));
  } catch {
    /* storage unavailable (private mode / quota) — the URL still carries the params */
  }
}

export function recallConnectParams(storage: StorageLike): ConnectParams | null {
  try {
    const raw = storage.getItem(CONNECT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { from?: unknown }).from === 'string' &&
      typeof (parsed as { connection?: unknown }).connection === 'string'
    ) {
      const { from, connection } = parsed as ConnectParams;
      return from && connection ? { from, connection } : null;
    }
    return null;
  } catch {
    return null;
  }
}

export function forgetConnectParams(storage: StorageLike): void {
  try {
    storage.removeItem(CONNECT_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** A company membership row as `GET /api/users/me/companies` returns it. */
export interface MembershipRow {
  id: string;
  name: string;
  role: string;
}

/** Design doc Q1: only owners and admins may connect a company. */
export function isAdminRole(role: string | null | undefined): boolean {
  const r = role?.toLowerCase();
  return r === 'owner' || r === 'admin';
}

export function adminMemberships(rows: ReadonlyArray<MembershipRow>): MembershipRow[] {
  return rows.filter((row) => isAdminRole(row.role));
}

/**
 * Which company the picker starts on: the active org when the user administers
 * it, otherwise the first company they administer.
 */
export function pickDefaultCompany(
  admins: ReadonlyArray<MembershipRow>,
  activeOrgId: string | null,
): string | null {
  if (admins.length === 0) return null;
  if (activeOrgId && admins.some((a) => a.id === activeOrgId)) return activeOrgId;
  return admins[0].id;
}

export type ConnectPageState =
  | { kind: 'loading' }
  | { kind: 'load-failed' }
  | { kind: 'invalid-link' }
  | { kind: 'unknown-product'; from: string }
  | { kind: 'no-company'; from: string }
  | { kind: 'not-admin'; from: string; companies: MembershipRow[] }
  | { kind: 'pick-company'; from: string; connection: string; admins: MembershipRow[]; defaultCompanyId: string };

/**
 * The acceptor page's whole decision tree, so the page component is only
 * rendering + the accept call.
 */
export function resolveConnectPageState(input: {
  params: ConnectParams | null;
  memberships: ReadonlyArray<MembershipRow> | null;
  activeOrgId: string | null;
  loading: boolean;
  /** The membership list request failed — a spinner forever is the wrong answer to a 500. */
  loadFailed?: boolean;
}): ConnectPageState {
  const { params, memberships, activeOrgId, loading, loadFailed = false } = input;
  if (!params) return { kind: 'invalid-link' };
  if (!isKnownProduct(params.from)) return { kind: 'unknown-product', from: params.from };
  if (loadFailed && memberships === null) return { kind: 'load-failed' };
  if (loading || memberships === null) return { kind: 'loading' };
  if (memberships.length === 0) return { kind: 'no-company', from: params.from };

  const admins = adminMemberships(memberships);
  if (admins.length === 0) return { kind: 'not-admin', from: params.from, companies: [...memberships] };

  const defaultCompanyId = pickDefaultCompany(admins, activeOrgId);
  return {
    kind: 'pick-company',
    from: params.from,
    connection: params.connection,
    admins,
    // pickDefaultCompany only returns null for an empty list, excluded above.
    defaultCompanyId: defaultCompanyId ?? admins[0].id,
  };
}

/**
 * Danish copy for the accept endpoint's failure statuses. 403 is bodiless
 * (`ForbidResult`), so the mapping is by status alone; 409 covers "already
 * connected", "no longer pending" and "expired", which all end the same way for
 * the user — start over from the other product.
 */
export function describeAcceptFailure(status: number, product: string): string {
  const label = productLabel(product);
  switch (status) {
    case 403:
      return (
        'Du kan ikke godkende denne forbindelse. Du skal være administrator for den valgte ' +
        `virksomhed, og forbindelsen skal være startet af dig. Log ind i ${label} med samme ` +
        'konto som her, og prøv igen.'
      );
    case 409:
      return (
        'Forbindelsen er udløbet, allerede godkendt, eller virksomheden er allerede forbundet ' +
        `til ${label}. Start forfra fra ${label}.`
      );
    case 502:
      return `${label} kunne ikke kontaktes lige nu. Prøv igen om et øjeblik.`;
    case 404:
      return 'Produktforbindelser er ikke slået til i Mesterplan endnu. Kontakt support.';
    default:
      return 'Forbindelsen kunne ikke oprettes. Prøv igen.';
  }
}

/** Danish copy for the initiator's failure statuses (the card's "Forbind" button). */
export function describeInitiateFailure(status: number, product: string): string {
  const label = productLabel(product);
  switch (status) {
    case 409:
      return `Der findes allerede en forbindelse til ${label}. Genindlæs siden.`;
    case 400:
      return `${label} er ikke konfigureret som forbindelse endnu. Kontakt support.`;
    case 404:
      return 'Produktforbindelser er ikke slået til i Mesterplan endnu. Kontakt support.';
    default:
      return `Kunne ikke starte forbindelsen til ${label}. Prøv igen.`;
  }
}
